/**
 * ocrApi — OCR(/ocr-hub) v2 백엔드 연동 (FE-1 목록 + FE-2 생성·업로드·처리).
 *
 * 책 PDF(문제/정답/풀이 여러 파일)를 프로젝트 단위로 업로드 → LLM OCR → 검수 →
 * 문제은행 승격하는 「OCR」 메뉴의 API 모듈.
 *
 * 응답 봉투: { status: 'success'|'error', message, status_code, data } — 성공 판정 res.status === 'success'.
 * 401 처리·베이스 URL은 utils/apiUtils.fetchWithTokenRefresh 공용(로컬 복사 금지 관례).
 * 예외: 파일 업로드(multipart)만 raw fetch — fetchWithTokenRefresh가 Content-Type: application/json을
 * 강제해 multipart boundary가 깨지므로 사용 금지(지시서 §2, SingleFileUploadModal 관례).
 */
import { fetchWithTokenRefresh, fetchAPI, API_BASE_URL } from '../../utils/apiUtils';

// 프로젝트 status 코드 → 라벨·뱃지 색 (WorkListTab.getStatusDisplay 스타일).
// 색 계열이 국면을 가른다: 진행 중(sky/blue/amber/purple/orange) vs 작업 없음(gray).
// 500 종료는 gray-600 — 초록(primary 계열 = '좋음')이면 승격 0건 force 종료와 정상 완료가
// 목록에서 같은 무게로 읽힌다. 생성됨(gray-800)보다 한 단 흐려 종료가 뒤로 물러난다.
// 100 생성됨 / 150 분리중 / 200 처리중(OCR) / 300 검수중 / 350 분석중 / 400 컨펌대기 / 500 종료
// status는 단계 진행도가 아니라 '프로젝트 국면'이다(백엔드 확인 2026-08-11).
// 전이 시점: 300→350 = 분석 큐잉 · 350→400 = **최초 승격 성공**(분석 완료가 아니다).
// 400에서도 검수·분석·추가 승격이 계속 가능하다 — 400을 '승격 완료'로 읽으면 안 된다.
// 단계 진행도는 프로젝트 상세의 집계 6종(problem_count 외)으로 판정한다.
export const OCR_PROJECT_STATUS = {
  100: { label: '생성됨', cls: 'bg-gray-100 text-gray-800' },
  150: { label: '분리중', cls: 'bg-sky-100 text-sky-800' },
  200: { label: '처리중', cls: 'bg-blue-100 text-blue-800' },
  300: { label: '검수중', cls: 'bg-amber-100 text-amber-800' },
  350: { label: '분석중', cls: 'bg-purple-100 text-purple-800' },
  400: { label: '컨펌대기', cls: 'bg-orange-100 text-orange-800' },
  500: { label: '종료', cls: 'bg-gray-100 text-gray-600' },
};

// 텍스트는 서버 status_label 우선(코드 추가 시 프론트 재배포 없이 표기), 색은 코드 매핑.
export function getProjectStatusDisplay(status, statusLabel) {
  const meta = OCR_PROJECT_STATUS[status];
  return {
    text: statusLabel || meta?.label || `상태 ${status}`,
    cls: meta?.cls || 'bg-gray-100 text-gray-800',
  };
}

// 파일 종류: 0 혼합 / 1 문제 / 2 풀이 / 3 정답
export const OCR_FILE_KIND = {
  0: { label: '혼합', cls: 'bg-gray-100 text-gray-800' },
  1: { label: '문제', cls: 'bg-blue-100 text-blue-800' },
  2: { label: '풀이', cls: 'bg-purple-100 text-purple-800' },
  3: { label: '정답', cls: 'bg-green-100 text-green-800' },
};

// 파일 상태: 100 대기 / 150 분리중 / 200 분리완료 / 500 실패 (프로젝트 status와 다른 체계)
export const OCR_FILE_STATUS = {
  100: { label: '대기', cls: 'bg-gray-100 text-gray-800' },
  150: { label: '분리중', cls: 'bg-sky-100 text-sky-800' },
  200: { label: '분리완료', cls: 'bg-green-100 text-green-800' },
  500: { label: '실패', cls: 'bg-red-100 text-red-800' },
};

export function getFileKindDisplay(kind, kindLabel) {
  const meta = OCR_FILE_KIND[kind];
  return { text: kindLabel || meta?.label || `종류 ${kind}`, cls: meta?.cls || 'bg-gray-100 text-gray-800' };
}

export function getFileStatusDisplay(status) {
  const meta = OCR_FILE_STATUS[status];
  return { text: meta?.label || `상태 ${status}`, cls: meta?.cls || 'bg-gray-100 text-gray-800' };
}

// 문제 매칭 상태: 100 본문만 / 200·210 부분(정답 또는 풀이) / 300 완전 / 400 충돌
export const OCR_MATCH_STATUS = {
  100: { label: '본문만', cls: 'bg-gray-100 text-gray-800' },
  200: { label: '부분', cls: 'bg-blue-100 text-blue-800' },
  210: { label: '부분', cls: 'bg-blue-100 text-blue-800' },
  300: { label: '완전', cls: 'bg-green-100 text-green-800' },
  400: { label: '충돌', cls: 'bg-red-100 text-red-800' },
};

export function getMatchStatusDisplay(status) {
  const meta = OCR_MATCH_STATUS[status];
  return { text: meta?.label || `매칭 ${status}`, cls: meta?.cls || 'bg-gray-100 text-gray-800' };
}

// 검수 상태: 100 검수대기(AI 생성 시 재검수로 복귀 포함) / 200 검수완료(승인) / 400 반려
// (수정요청 F5: 검수완료 300은 오기 — 백엔드 정본 200으로 정정)
export const OCR_REVIEW_STATUS = {
  100: { label: '검수대기', cls: 'bg-amber-50 text-amber-700' },
  200: { label: '검수완료', cls: 'bg-green-100 text-green-800' },
  400: { label: '반려', cls: 'bg-red-100 text-red-800' },
};

export function getReviewStatusDisplay(status) {
  const meta = OCR_REVIEW_STATUS[status];
  return { text: meta?.label || `상태 ${status}`, cls: meta?.cls || 'bg-gray-100 text-gray-800' };
}

// 문제 단위 AI 분석 상태 (수정요청 F6 정본): 100 미실행 / 110 대기(큐) / 150 처리중 / 200 완료 / 500 실패
export const OCR_ANALYSIS_STATUS = {
  100: { label: '분석 미실행', cls: 'bg-gray-100 text-gray-500' },
  110: { label: '분석대기', cls: 'bg-gray-100 text-gray-800' },
  150: { label: '분석중', cls: 'bg-blue-100 text-blue-800' },
  200: { label: '분석완료', cls: 'bg-green-100 text-green-800' },
  500: { label: '분석실패', cls: 'bg-red-100 text-red-800' },
};

export function getAnalysisStatusDisplay(status) {
  if (status == null) return { text: '분석 미실행', cls: 'bg-gray-100 text-gray-500' };
  const meta = OCR_ANALYSIS_STATUS[status];
  return { text: meta?.label || `분석 ${status}`, cls: meta?.cls || 'bg-gray-100 text-gray-800' };
}

// 문항 유형 — body_payload.answerType. OCR이 채워 주는 값이라 본문 문자열을 뒤질 필요가 없다.
// 문제 상세와 목록 items[] 양쪽에 있다(목록은 2026-08-11 추가 — 백엔드 커밋 6189a31).
export const OCR_ANSWER_TYPE = {
  multiple_choice: { label: '객관식', cls: 'bg-slate-100 text-slate-700' },
  descriptive: { label: '서답형', cls: 'bg-indigo-100 text-indigo-700' },
  short_answer: { label: '단답형', cls: 'bg-indigo-100 text-indigo-700' },
};

export function getAnswerTypeDisplay(type) {
  if (!type) return null;
  const meta = OCR_ANSWER_TYPE[type];
  return { text: meta?.label || String(type), cls: meta?.cls || 'bg-gray-100 text-gray-700' };
}

// 무결성 플래그 칩 — 미지의 flag 코드는 원문 그대로 표기(무시 금지, 지시서 §4)
// blocking = 승인·승격을 막을 수 있는 플래그(추정). 풀이없음만 명시적으로 허용된다
// ("풀이 없음은 승격 허용" — FE-5 지시서). 최종 판정은 백엔드가 하고 409 message로 알린다.
//
// 아래 넷은 2026-09-25 백엔드 회신(BE-20260925-ocr-field-report-fixes.md §2-6, dev 1e4eb15)으로 생긴
// **검수 참고용 경고**다 — 서버 차단 목록(bulkocr_bind.BLOCKING_FLAGS: 위 blocking 넷)에 없고 승인·등록을 막지 않는다.
// 메타에 없으면 getBlockingFlags 가 미지 코드로 보고 「승인 불가」로 칠하므로 **서버 배포와 같은 창에** 있어야 한다.
// hint 는 검수 창 「확인할 점」 줄과 칩 title 이 쓴다 — 무엇을 대조해야 하는지까지 말한다.
export const OCR_FLAG_META = {
  missing_answer: { label: '정답없음', cls: 'bg-red-100 text-red-700', blocking: true },
  missing_solution: { label: '풀이없음', cls: 'bg-yellow-100 text-yellow-800' },
  orphan: { label: '본문없음', cls: 'bg-red-100 text-red-700', blocking: true },
  duplicate_body: { label: '본문중복', cls: 'bg-red-100 text-red-700', blocking: true },
  answer_conflict: { label: '정답충돌', cls: 'bg-red-100 text-red-700', blocking: true },
  answer_suspect: {
    label: '정답 의심', cls: 'bg-yellow-100 text-yellow-800', blocking: false,
    hint: '정답지 칸을 잘못 읽었을 수 있습니다(선지 번호가 아니거나 배점 칸과 같음) — 원본 정답지와 대조하세요. 정답을 고치면 사라집니다',
  },
  answer_solution_mismatch: {
    label: '풀이·정답 불일치', cls: 'bg-yellow-100 text-yellow-800', blocking: false,
    hint: '풀이가 도달한 답이 정답과 다릅니다 — 원본과 대조하세요',
  },
  ai_figure_unseen: {
    label: '그림 없이 분석', cls: 'bg-yellow-100 text-yellow-800', blocking: false,
    hint: '그림을 보지 못하고 푼 정답·풀이입니다 — 그림과 대조하세요',
  },
  ocr_reduced_thinking: {
    label: 'OCR 정확도 낮음', cls: 'bg-yellow-100 text-yellow-800', blocking: false,
    hint: 'AI가 이 쪽을 한 번에 다 읽지 못해 간단히 다시 읽은 본문입니다. 그림이 빠지거나 선택지가 틀렸을 수 있으니 원본과 비교하고, 빠진 게 있으면 [이 문제 다시 OCR]을 누르세요',
  },
};

export function getFlagDisplay(code) {
  const meta = OCR_FLAG_META[code];
  return {
    text: meta?.label || String(code),
    cls: meta?.cls || 'bg-gray-100 text-gray-700',
    hint: meta?.hint || null,
  };
}

// 승인 전 '거부될 수 있음' 경고에만 쓰는 목록 — 이것으로 버튼을 막지 않는다.
// 차단 조건은 백엔드가 정하므로 프론트가 단정하면 정당한 승인까지 막게 된다.
// 미지의 코드는 차단 여부를 알 수 없어 경고에 포함한다(지시서 §4 "미지 flag 무시 금지").
export function getBlockingFlags(flags) {
  return (Array.isArray(flags) ? flags : []).filter((code) =>
    OCR_FLAG_META[code] ? !!OCR_FLAG_META[code].blocking : true,
  );
}

// 봉투 검증 공통 — 성공 봉투가 아니면 서버 message를 표면화
function unwrap(res, fallbackMsg) {
  if (!res || res.status !== 'success') {
    throw new Error(res?.message || fallbackMsg);
  }
  return res.data;
}

// ── 라벨 ──
// 라벨 문자열 하나를 토큰으로 쪼갠다 — 콤마가 구분자다(ProjectCreateModal 키워드 입력과 같은 관례).
// 승격 라벨 v1이 콤마 합산 문자열이라 라벨 안에 콤마가 남으면 v1/v2가 어긋난다.
export const splitLabelText = (text) => String(text).split(',').map((s) => s.trim()).filter(Boolean);

// 서버가 준 개별 라벨을 문자열 배열로 고른다.
// PATCH 계약(extra_labels)은 문자열 배열이지만 GET 응답의 모양은 문서에 없다 —
// 같은 백엔드의 문제은행 v2 상세가 labels[]{label_name} 과 labels_raw(콤마 문자열)를
// 함께 쓰고 있어(BE-20260825-problem-hub-integrated.md §③) 세 모양을 다 받는다.
// 모양이 하나만 달라도 통째로 버리면, DB에는 값이 있는데 화면은 「없음」이라 말하게 된다.
export function toLabelList(v) {
  if (Array.isArray(v)) {
    return v
      .map((x) => (x != null && typeof x === 'object' ? (x.label_name ?? x.name ?? '') : x))
      .map((x) => String(x ?? '').trim())
      .filter(Boolean);
  }
  if (typeof v === 'string') return splitLabelText(v);
  return [];
}

// ── 프로젝트 목록 ──
// GET /api/v2/ocr/projects?page=&per_page=  (per_page 5~100 가변)
// data = { items: [{ project_id, project_uuid, name, keywords, status, status_label,
//          file_count, total_pages, split_pages, extracted_pages, failed_pages, created_at,
//          default_prompt }],
//          page, per_page, total_items, total_pages }
// default_prompt = 프로젝트 기본 지시(BE-20260829-reply-fe-0828.md §4-2 5-4). 없으면 null.
// 항목을 통째로 넘기므로(items 그대로) 새 필드는 자동으로 화면까지 간다 — 골라 담지 않는다.
// 빈 목록은 items: [] (404 아님).
export async function fetchOcrProjects({ page = 1, perPage = 20 } = {}, getAccessToken) {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  const res = await fetchWithTokenRefresh(`/api/v2/ocr/projects?${params}`, {}, getAccessToken);
  if (!res || res.status !== 'success') {
    throw new Error(res?.message || 'OCR 프로젝트 목록을 불러오지 못했습니다.');
  }
  const d = res.data || {};
  return {
    items: Array.isArray(d.items) ? d.items : [],
    page: d.page || page,
    perPage: d.per_page || perPage,
    totalItems: d.total_items ?? 0,
    totalPages: d.total_pages ?? 0,
  };
}

// ── 프로젝트 생성 ──
// POST /api/v2/ocr/projects — { name(≤200 필수), keywords: string[], numbering_mode: 0 단일연속|1 단원별재시작 }
export async function createProject({ name, keywords, numberingMode }, getAccessToken) {
  const res = await fetchWithTokenRefresh('/api/v2/ocr/projects', {
    method: 'POST',
    body: JSON.stringify({
      name,
      keywords: Array.isArray(keywords) ? keywords : [],
      numbering_mode: numberingMode ?? 0,
    }),
  }, getAccessToken);
  return unwrap(res, '프로젝트 생성에 실패했습니다.');
}

// ── 프로젝트 상세 ──
// GET /api/v2/ocr/projects/{id} — data = 프로젝트 필드 + files[](file_id, kind, status, split/extracted 카운트)
// 프로젝트 필드에 default_prompt(기본 지시, 없으면 null)가 포함된다
// — BE-20260829-reply-fe-0828.md §4-2 5-4. data 를 통째로 돌려주므로 여기서 손댈 것은 없다.
export async function getProjectDetail(projectId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`/api/v2/ocr/projects/${projectId}`, {}, getAccessToken);
  return unwrap(res, '프로젝트 정보를 불러오지 못했습니다.');
}

// ── PDF 업로드 (multipart) ──
// POST /api/v2/ocr/projects/{id}/files — FormData: file=PDF 1개, kind='0'|'1'|'2'|'3'.
// 여러 파일은 반복 호출(파일마다 종류가 다름). 컨펌대기/종료(status≥400) 프로젝트는 423.
// multipart라 fetchWithTokenRefresh 사용 금지 → raw fetch + Authorization (SingleFileUploadModal 관례).
export async function uploadProjectFile(projectId, file, kind, getAccessToken) {
  const token = await getAccessToken();
  if (!token) {
    window.location.href = '/signin';
    return null;
  }
  const formData = new FormData();
  formData.append('file', file);
  formData.append('kind', String(kind));
  let response;
  try {
    response = await fetch(`${API_BASE_URL}/api/v2/ocr/projects/${projectId}/files`, {
      method: 'POST',
      headers: { Authorization: token },
      body: formData,
      credentials: 'include',
    });
  } catch (netErr) {
    // fetch 거부는 네트워크/CORS 단계 실패 — 공용 유틸(apiUtils)과 동일한 한글 메시지로 변환
    throw new Error('서버에 연결할 수 없습니다. 인터넷 연결을 확인하거나 잠시 후 다시 시도해주세요.');
  }
  if (response.status === 401) {
    // 공용 유틸과 같은 뒷정리 — 죽은 토큰을 남겨 두면 다음 화면이 또 401 을 맞는다
    // (계약 §6: soft-delete 된 사용자의 유효 토큰이 이제 401 이라 이 갈래가 실제로 돌게 됐다).
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.href = '/signin';
    return null;
  }
  if (response.status === 423) {
    throw new Error('컨펌 대기/종료 상태의 프로젝트에는 파일을 추가할 수 없습니다.');
  }
  let body = null;
  try { body = await response.json(); } catch (_) { /* 비JSON 응답 → 아래에서 상태코드로 표면화 */ }
  if (!response.ok || !body || body.status !== 'success') {
    throw new Error(body?.message || `파일 업로드에 실패했습니다. (${response.status})`);
  }
  return body.data;
}

// ── 처리 실행: 낱장 분리(동기) + OCR 대기열 적재 ──
// POST /api/v2/ocr/projects/{id}/run — body 전부 옵션 { file_id, page_range: [s, e], user_prompt, force }.
// file_id 없으면 전체 파일, page_range 없으면 파일 전체.
// page_range 계약 확정(BE 회신 d773f3b): **0-base·양끝 포함** — [0,0] = 첫 장 하나.
// 무효 범위(타입·음수·역순)는 400, 쪽수 밖 범위는 200 + target_pages:[] (파일별 쪽수가 달라).
// 스킵(Q4): 완료(200)·처리중(150)은 재큐잉 안 됨(재과금 방지) — 실패(500)·미큐잉(105)만 다시 돈다.
// 예외 force:true(BE-20260914-reply-page-reocr-force.md, dev 3435dc0) — 완료(200)도 다시 읽는다.
// file_id + 한 장 page_range([n,n])일 때만 허용(아니면 400) · 호출 1회 = 최대 1쪽 · 처리중은 force 여도 스킵.
// 응답에 target_pages(서버가 해석한 0-base 목록)·new_split·skipped_pages 추가됨.
// 취소: POST /projects/{id}/run/cancel 신설(대기 회수, 처리중은 중단 불가) — 화면 부착은 추후.
// ⚠ 분리가 동기라 큰 PDF는 응답이 수십 초(서버 timeout 300s) — 호출부에서 버튼 로딩 처리 필수.
//
// 종전에는 fetchWithTokenRefresh 를 썼는데, 그쪽은 400 을 「잘못된 요청입니다. 입력 정보를
// 확인해주세요.」로 갈아 끼워 서버 message 를 삼킨다(utils/apiUtils.js:73). user_prompt 상한이
// 확정되면서(500자 초과 400 — BE-20260829-reply-fe-0828.md §4-2 5-2) 「현재 N자」가 그 message
// 에만 담기게 됐고, 삼키면 사람이 무엇을 고쳐야 할지 알 수 없다. 그래서 이 파일의 다른 쓰기
// 함수들과 같은 requestJsonWithStatus 로 옮겼다 — 상태코드(err.httpStatus)와 서버 message 를 살린다.
// ⚠ 그 대가로 401 의 반환값이 달라진다: 종전엔 unwrap 이 throw 했지만 이제는 null 이 온다.
//    호출부(ProjectDetailPanel.runPages)가 null 을 잡아 던진다 — 「적재 0건」으로 읽히면 거짓이다.
//
// **2026-09-25 (BE-20260925 §2-3 F10)**: force 가 아닌 실행에서 user_prompt 키가 **없으면 그 쪽의 직전 지시를 유지**한다
// (종전엔 지웠다). 지우려면 빈 문자열을 보낸다 — clearPrompt:true 가 그 일을 한다(지시를 새로 적었으면 그것이 이긴다).
// force(검수 창 한 쪽 다시 읽기)는 종전 그대로 — 비우면 서버가 지운다.
export async function runProject(projectId, {
  fileId, pageRange, userPrompt, force, clearPrompt,
} = {}, getAccessToken) {
  const payload = {};
  if (fileId != null) payload.file_id = fileId;
  if (Array.isArray(pageRange)) payload.page_range = pageRange;
  // 완료된 쪽 다시 읽기 — **참(true)일 때만** 싣는다(BE-20260914-reply-page-reocr-force.md §2).
  // 서버는 bool 이 아니면 400, file_id 와 한 장 page_range([n,n])가 함께 없으면 400 이다.
  // 싣는 곳은 검수 모달의 [이 페이지 다시 OCR] 한 경로다(§4-1). ②의 실행에 섞이면 완료된 쪽이 다시 과금된다.
  if (force === true) payload.force = true;
  // 사용자 지시(선택) — 이 실행이 읽는 **모든 쪽**에 걸린다. 비면 키를 싣지 않는다.
  // 서버가 받는다(BE-20260829-reply-fe-0828.md §4-1 표). strip 후 500자 이하 · 초과·비문자열은 400.
  // 저장 자리는 이번 실행 대상 페이지의 ocrpage.user_prompt(페이지 llm_raw.user_prompt)다.
  // 비면 프로젝트 default_prompt 가 대신 쓰인다(§4-2 5-4).
  // 붙는 자리는 프롬프트 말미의 [추가 지시] 블록이고 옵션·규칙이 우선한다(§4-2 6-2).
  // 단 그림 후처리(figure pass)에는 붙지 않는다 — 본문·풀이·정답 읽기 프롬프트에만 걸린다(§4-2 6-2).
  if (userPrompt && userPrompt.trim()) payload.user_prompt = userPrompt.trim();
  else if (clearPrompt === true && force !== true) payload.user_prompt = '';
  return requestJsonWithStatus(
    `/api/v2/ocr/projects/${projectId}/run`,
    { method: 'POST', payload },
    getAccessToken,
    '처리 실행에 실패했습니다.',
  );
}

// ── 상태코드 분기가 필요한 JSON 요청 공용 헬퍼 ──
// fetchWithTokenRefresh는 409/423 등의 상태코드·서버 message를 구조적으로 노출하지 않으므로,
// 호출부가 httpStatus로 분기해야 하는 쓰기 API(bind·patch·review·analyze)는 이 헬퍼를 쓴다.
// 실패 시 Error에 httpStatus를 실어 throw. 토큰 부재/401은 /signin 리다이렉트 후 null 반환.
async function requestJsonWithStatus(path, { method = 'POST', payload } = {}, getAccessToken, fallbackMsg) {
  const token = await getAccessToken();
  if (!token) {
    window.location.href = '/signin';
    return null;
  }
  let response;
  try {
    response = await fetchAPI(path, {
      method,
      headers: { Authorization: token, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload ?? {}),
    });
  } catch (netErr) {
    // fetch 거부는 네트워크/CORS 단계 실패 — 공용 유틸(apiUtils)과 동일한 한글 메시지로 변환
    throw new Error('서버에 연결할 수 없습니다. 인터넷 연결을 확인하거나 잠시 후 다시 시도해주세요.');
  }
  if (response.status === 401) {
    // 공용 유틸과 같은 뒷정리 — 죽은 토큰을 남겨 두면 다음 화면이 또 401 을 맞는다
    // (계약 §6: soft-delete 된 사용자의 유효 토큰이 이제 401 이라 이 갈래가 실제로 돌게 됐다).
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.href = '/signin';
    return null;
  }
  let body = null;
  try { body = await response.json(); } catch (_) { /* 비JSON → 아래에서 상태코드로 표면화 */ }
  if (!response.ok || !body || body.status !== 'success') {
    const err = new Error(body?.message || `${fallbackMsg} (${response.status})`);
    err.httpStatus = response.status;
    // 오류 봉투의 data — 기계가 읽을 사유(data.reason)와 그 부속 값이 여기 실린다
    // (예: 종료 409 unpromoted_reviewed 의 단계별 수 · 매칭 409 failed_pages 의 쪽 목록).
    // 종전엔 message·httpStatus 만 넘겨 사유를 가를 수 없었다(BE-20260925 §2-4 ⚠). 없으면 null.
    err.data = body?.data ?? null;
    throw err;
  }
  return body.data;
}

// ── 매칭(제본) 실행 ──
// POST /api/v2/ocr/projects/{id}/bind — body 옵션 { exclude_failed: true }.
// 성공 시 프로젝트 status → 300(검수중), data에 reconcile_report(조율 리포트) 포함. 재실행 안전(검수 완료분 보존).
// 에러: 423 = 전체 OCR 미완료 또는 컨펌대기/종료 / 409 = 실패 페이지 존재 + exclude_failed 미지정(confirm 후 재호출).
export async function bindProject(projectId, { excludeFailed } = {}, getAccessToken) {
  return requestJsonWithStatus(
    `/api/v2/ocr/projects/${projectId}/bind`,
    { method: 'POST', payload: excludeFailed ? { exclude_failed: true } : {} },
    getAccessToken,
    '매칭 실행에 실패했습니다.',
  );
}

// ── 문제 목록 (검수 대상) ──
// GET /api/v2/ocr/projects/{id}/problems?page=&per_page=&match_status=&review_status=&has_flags=true
// items[]: { ocr_prob_id, problem_number, scope, order_no, match_status, review_status,
//            promoted_problem_id, promote_count, promoted_at, needs_repromote,  ← 2026-08-30 신규
//            has_body/has_answer/has_solution, flags[], body_preview(stem 앞 60자·LaTeX 원문|null),
//            is_split(번호 충돌로 분리된 행) } — 정렬: 책 순서(order_no)
export async function fetchProjectProblems(projectId, { page = 1, perPage = 20, matchStatus, reviewStatus, hasFlags } = {}, getAccessToken) {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  if (matchStatus != null) params.append('match_status', String(matchStatus));
  if (reviewStatus != null) params.append('review_status', String(reviewStatus));
  if (hasFlags) params.append('has_flags', 'true');
  const res = await fetchWithTokenRefresh(`/api/v2/ocr/projects/${projectId}/problems?${params}`, {}, getAccessToken);
  const d = unwrap(res, '문제 목록을 불러오지 못했습니다.') || {};
  return {
    items: Array.isArray(d.items) ? d.items : [],
    page: d.page || page,
    totalItems: d.total_items ?? 0,
    totalPages: d.total_pages ?? 0,
  };
}

// ── 문제 전량 조회 (번호 지도·일괄 번호 수정용) ──
// 번호 중복과 빈 번호는 프로젝트 전체를 봐야 판정할 수 있어 페이지 단위로는 알 수 없다.
// 목록 API를 per_page 최대치(100)로 순회해 모은다. 폭주 방지로 MAX_PAGES에서 끊고 truncated로 알린다
// (그 경우 지도는 '일부만 반영됨'을 표시해야 한다 — 조용히 잘라내면 없는 번호를 비었다고 말하게 된다).
const ALL_PER_PAGE = 100;
const ALL_MAX_PAGES = 20;

export async function fetchAllProjectProblems(projectId, getAccessToken, opts = {}) {
  const all = [];
  let page = 1;
  let totalPages = 1;
  let truncated = false;
  do {
    // eslint-disable-next-line no-await-in-loop
    const d = await fetchProjectProblems(projectId, { ...opts, page, perPage: ALL_PER_PAGE }, getAccessToken);
    all.push(...d.items);
    totalPages = d.totalPages || 1;
    page += 1;
    if (page > ALL_MAX_PAGES) {
      truncated = page <= totalPages;
      break;
    }
  } while (page <= totalPages);
  return { items: all, truncated };
}

// ── 문제 상세 (검수 패널) ──
// GET /api/v2/ocr/problems/{ocr_prob_id} — body_payload/answer/solution_payload/concept_ids/difficulty
// + ai_generated({answer,solution} — AI 생성 표시, null=전부 원본 추출)
// + sources[]({kind 1문제/2풀이/3정답, page_id, page_index, file_name, bbox[0~1 정규화 x0,y0,x1,y1]|null})
export async function fetchProblemDetail(ocrProbId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`/api/v2/ocr/problems/${ocrProbId}`, {}, getAccessToken);
  return unwrap(res, '문제 상세를 불러오지 못했습니다.');
}

// ── 페이지 이미지 presigned URL ──
// GET /api/v2/ocr/pages/{page_id}/image-url → { page_id, url, expires_in } — 만료되므로 열 때마다 새로 요청.
export async function fetchPageImageUrl(pageId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`/api/v2/ocr/pages/${pageId}/image-url`, {}, getAccessToken);
  return unwrap(res, '페이지 이미지를 불러오지 못했습니다.');
}

// ── 프로젝트 페이지 목록 ──
// GET /api/v2/ocr/projects/{id}/pages?file_id=&page=&per_page=
// items[]: { page_id, page_uuid, file_uuid, page_index, status, page_type, has_image, error_msg }
// 검수 화면의 '원본 페이지 넘기기'가 쓴다. 예전에는 page_id가 파일 안에서 연속이라는 관찰에 기대어
// 'id - index + 목표index'로 이웃 id를 지어냈는데, 파일이 여럿이면 경계에서 남의 파일 페이지를 열게 된다.
export async function fetchProjectPages(projectId, { page = 1, perPage = 100, fileId } = {}, getAccessToken) {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  if (fileId != null) params.append('file_id', String(fileId));
  const res = await fetchWithTokenRefresh(`/api/v2/ocr/projects/${projectId}/pages?${params}`, {}, getAccessToken);
  const d = unwrap(res, '페이지 목록을 불러오지 못했습니다.') || {};
  return { items: Array.isArray(d.items) ? d.items : [], totalPages: d.total_pages ?? 0 };
}

// 전량 — 이웃 페이지를 찾으려면 파일 경계를 알아야 하고, 그건 프로젝트 전체를 봐야 안다.
const PAGES_MAX = 20; // per_page 100 × 20 = 2000페이지. 넘으면 넘기기만 제한된다(모달은 그대로 뜬다)
export async function fetchAllProjectPages(projectId, getAccessToken) {
  const all = [];
  let page = 1;
  let totalPages = 1;
  do {
    // eslint-disable-next-line no-await-in-loop
    const d = await fetchProjectPages(projectId, { page, perPage: 100 }, getAccessToken);
    all.push(...d.items);
    totalPages = d.totalPages || 1;
    page += 1;
    if (page > PAGES_MAX) break;
  } while (page <= totalPages);
  return all;
}

// ── 문제 삭제 ──
// DELETE /api/v2/ocr/problems/{ocr_prob_id} → { ocr_prob_id, deleted: true } (soft delete)
// 423 = 승격된 행 / 종료(500) 프로젝트.
// 같은 번호 그룹에 본문 행이 홀로 남으면 그 행의 duplicate_body는 백엔드가 자동으로 푼다.
export async function deleteProblem(ocrProbId, getAccessToken) {
  return requestJsonWithStatus(
    `/api/v2/ocr/problems/${ocrProbId}`,
    { method: 'DELETE' },
    getAccessToken,
    '문제 삭제에 실패했습니다.',
  );
}

// ── 검수 수정 ──
// PATCH /api/v2/ocr/problems/{id} — 보낸 키만 반영(answer/problem_number/body_payload/solution_payload/
// concept_ids/difficulty/extra_labels + expected_updated_at). 응답 data = 갱신된 상세 전문(화면 상태를 이걸로 교체).
// 보낸 키의 값은 전체 치환(부분 병합 아님 — BE 회신 Q3). expected_updated_at(옵션)을 상세의
// updated_at 그대로 되돌려 보내면 그 사이 타처 수정 시 409("다른 곳에서 먼저 수정…" — Q8).
// 409 = problem_number 중복(message에 기존 문제 id) 또는 동시 수정 → message로 구분해 토스트.
export async function patchProblem(ocrProbId, patch, getAccessToken) {
  return requestJsonWithStatus(
    `/api/v2/ocr/problems/${ocrProbId}`,
    { method: 'PATCH', payload: patch },
    getAccessToken,
    '검수 수정에 실패했습니다.',
  );
}

// ── 프로젝트 설정 수정 ──
// PATCH /api/v2/ocr/projects/{id} — { default_prompt?: string|null, keywords?: string[] }.
// keywords 는 2026-08-30 에 열렸다(BE-20260830 §3-2) — 전체 치환, [] = 비움, 종료 프로젝트는 423.
// ⚠ 바꾼 키워드는 **이미 등록된 문제에 소급되지 않는다**. 서버는 재승격할 때 반영하는데
// 이 저장소는 재승격을 화면에서 유발하지 않으므로(오너 결정), 실질적으로 **앞으로 등록되는
// 문제에만** 붙는다. 화면 문구가 그렇게 말해야 한다.
// (BE-20260829-reply-fe-0828.md §0 「새 API」 · §4-2 5-4)
// null·빈 문자열·공백만 = 해제(키를 안 보낸 것과 같다). 종료(500) 프로젝트는 423.
// 값은 여기서 다듬지 않는다 — 이 파일의 user_prompt 함수 셋(run·reocr·analyze)은 「비면 키를
// 아예 싣지 않는다」인데, default_prompt 는 **빈 값 자체가 해제 명령**이라 그 관례를 그대로 베끼면
// 해제가 영원히 안 된다. 그래서 호출부가 { default_prompt: text.trim() || null } 로 조립해 넘긴다.
// 423 등의 분기는 호출부가 e.httpStatus 로 한다(이 파일의 다른 쓰기 함수와 같은 관례).
//
// ⚠ 응답 data 의 모양(프로젝트 상세 전문인가 default_prompt 하나인가)은 회신에 적혀 있지 않다.
//    그래서 화면은 이 반환값으로 상태를 갈아끼우지 않고, 저장 뒤 상세를 다시 읽는다 — 어느 쪽이든 옳다.
export async function patchProject(projectId, patch, getAccessToken) {
  return requestJsonWithStatus(
    `/api/v2/ocr/projects/${projectId}`,
    { method: 'PATCH', payload: patch },
    getAccessToken,
    '프로젝트 설정 저장에 실패했습니다.',
  );
}

// ── 이 문제 다시 OCR (동기) ──
// POST /api/v2/ocr/problems/{id}/reocr — body { kinds?: [1,2,3] } (1 본문 · 2 풀이 · 3 정답,
// 생략 시 sources[] 전부). 그 문제의 원본 영역만 크롭해 단건 재추출 — 행 신설·타 문제 영향 없음.
// review_status 유지 / AI 산출물 제거 + analysis_status→100(반려 규칙 — 사람이 넣은 값 보존) /
// 분석 자동 연결 없음. 응답 = GET /problems/{id}와 동일한 갱신 상세 전문(화면 교체용).
// 오류: 404 없음·삭제 / 423 승격·종료 / 409 같은 문제 진행 중 / 422 원본 참조·이미지 없음
// (S3 만료 포함 — message 그대로 표시) / 502 LLM 실패. (계약: ocr-editor-backend-ready.md §1)
// userPrompt(선택) — 「선지가 6개다」처럼 이 문제에만 통하는 지시. 비면 키를 싣지 않는다.
// 서버가 받는다(BE-20260829-reply-fe-0828.md §4-1 표). strip 후 500자 이하 · 초과·비문자열은 400.
// 그 문제의 reocr_prompt 로 남아 상세에 실려 온다. 비면 프로젝트 default_prompt 가 대신 쓰인다(§4-2 5-4).
// 붙는 자리는 프롬프트 말미의 [추가 지시] 블록이고, 그림 후처리에는 붙지 않는다(§4-2 6-2).
export async function reocrProblem(ocrProbId, { kinds, userPrompt } = {}, getAccessToken) {
  const payload = {};
  if (Array.isArray(kinds) && kinds.length > 0) payload.kinds = kinds;
  if (userPrompt && userPrompt.trim()) payload.user_prompt = userPrompt.trim();
  return requestJsonWithStatus(
    `/api/v2/ocr/problems/${ocrProbId}/reocr`,
    { method: 'POST', payload },
    getAccessToken,
    '다시 OCR에 실패했습니다.',
  );
}

// ── OCR 이 읽은 값을 라벨로 굳히기 ──
// problem_number 에는 OCR 이 지면에서 읽은 번호가 들어온다. 그래서 번호가 두 벌인 교재
// (선택형 1~16 + 서답형 1~5)는 매칭하면 1~5가 겹친다. 프런트는 그 값을 '원본 등장 순서'로
// 재정의하고 1..N 으로 다시 매기는데(numberPlan.planReindexByOrder), 그 순간 지면 번호는
// 사라진다 — 되돌릴 원본이 어디에도 없다. 본문 stem 에도 남지 않는다(2026-08-28 오너 확인.
// docs/ocr-hub-v2/ocr-bind-duplicate-rows-backend-handoff.md:130 의 「stem 에 남는다」는
// 프런트가 요청서에 쓴 가정이고 회신으로 확인된 적이 없다).
// 그래서 덮기 직전에 「24번」 라벨로 굳혀 승격 뒤에도 교재 번호로 문제를 찾을 수 있게 한다.
//
// 번호만이 아니다. OCR 은 배점(body_payload.points)과 문항 유형(body_payload.answerType)도
// 지면에서 읽어 두는데, 문제은행 상세 응답에는 그 두 필드가 없다
// (BE-20260825-problem-hub-integrated.md §3 — problem_id·author·status·labels_raw·origin_*·
// body_lexical·answer·solution_lexical·concept_ids·difficulty 가 전부다).
// 라벨로 굳히지 않으면 승격과 함께 버려진다. 라벨 종류가 「교육과정·교재·기타」 3종이고
// 개념·난이도는 명시적으로 배제되므로(ocr-review-editor-plan.html §라벨이란), 배점·유형은
// 「기타」로 들어갈 자리가 있고 개념·난이도는 여기에 넣지 않는다 — 그 둘은 전용 필드로 남는다.
//
// extra_labels 는 전체 치환이라(BE 회신 Q3) 현재 값을 읽어 합쳐 보낸다. 목록 응답에는
// extra_labels 가 없어 상세를 한 건씩 읽는다 — 읽지 않고 덮으면 검수자가 단 라벨이 날아간다.
//
// rows: [{ id, printed, scope }] · opts.withPrintedNumber: 지면 번호도 굳힐지(첫 매칭에서만 참).
// 배점·유형은 덮이는 값이 아니라 언제 굳혀도 안전하므로 그 제약을 받지 않는다.
// 반환 { done, total, added, skipped, aborted, failed } — added 는 라벨 수가 아니라 문제 수다.
//
// PRINTED_NUMBER_LABEL: 「24번」 꼴 — 이 행이 이미 번호 라벨을 가졌는지 판정한다.
// 한 건이라도 실패하면 즉시 멈춘다. 여기서 멈추지 않고 재배치로 넘어가면 되돌릴 수 없는 값이
// 사라지므로, 호출부는 failed 가 있으면 번호를 건드리지 않아야 한다.
export const PRINTED_NUMBER_LABEL = /^\d+번$/;

// 한 문제에 붙일 라벨의 글자 예산(콤마 포함).
// v1 problems.labels 가 콤마 합산 문자열이고 255자가 한계다(v2-records-labels-verification-plan.md
// B1-5 의 255자 409). 승격 때 프로젝트 공통 키워드까지 합쳐지므로 여기서 여유를 두고 끊는다 —
// 넘치면 409 로 굳히기 전체가 멈추고, 재시도해도 같은 자리에서 다시 멈춘다.
// 예산에 걸리면 뒤쪽 후보부터 빠진다. 그래서 넣는 순서가 곧 우선순위다:
// 지면 번호 > 배점 > 유형 > 단원 > 쪽수 > 파일명.
const LABEL_BUDGET = 200;

export async function freezeOcrLabels(rows, getAccessToken, onProgress, { withPrintedNumber = true } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  let done = 0;
  let added = 0;
  let skipped = 0;
  const tick = () => { done += 1; if (onProgress) onProgress(done, list.length); };
  for (const row of list) {
    const id = row?.id;
    if (id == null) { skipped += 1; tick(); continue; }
    let detail;
    try {
      // eslint-disable-next-line no-await-in-loop
      detail = await fetchProblemDetail(id, getAccessToken);
    } catch (e) {
      return { done, total: list.length, added, skipped, aborted: false, failed: e.message || '문제 상세를 읽지 못했습니다.' };
    }
    const cur = toLabelList(detail?.extra_labels);
    const next = [...cur];
    const add = (v) => {
      if (!v || next.includes(v)) return;
      if ([...next, v].join(',').length > LABEL_BUDGET) return; // 예산 초과 — 뒤쪽 후보부터 빠진다
      next.push(v);
    };

    // ① 지면 번호. 「N번」이 이미 하나라도 있으면 손대지 않는다 — 같은 라벨인지(includes)만
    // 보면 재매칭에서 가짜가 박힌다. 페이지가 추가되면 order_no 순서가 밀려 이미 정리된 행의
    // problem_number(=인덱스)를 지면 번호로 오인한다. 첫 매칭에서 「1번」을 굳힌 서답형이
    // 재매칭에서 「17번」까지 갖게 되는 식이다. 검수자가 「24번」을 손으로 쳐 둔 행도 건너뛴다 —
    // 그 값 역시 지면 번호일 가능성이 높고, 애매하면 가짜를 박기보다 안 붙이는 쪽으로 실패한다.
    const printed = Number(row?.printed);
    if (withPrintedNumber && Number.isInteger(printed)
        && !cur.some((v) => PRINTED_NUMBER_LABEL.test(v))) {
      add(`${printed}번`);
    }
    // ② 배점 — OCR 이 지면에서 읽은 값. 소수 배점(3.9)도 그대로 쓴다(수능 배점 관례).
    const points = Number(detail?.body_payload?.points);
    if (Number.isFinite(points) && points > 0) add(`${points}점`);
    // ③ 문항 유형 — 아는 세 종류만 넣는다. 미지 코드를 그대로 넣으면 영문 토큰이 라벨이 된다.
    const typeLabel = OCR_ANSWER_TYPE[detail?.body_payload?.answerType]?.label;
    if (typeLabel) add(typeLabel);
    // ④ 단원 — numbering_mode=1(단원별 재시작) 프로젝트에만 값이 있다.
    // '__single__' 은 단원 없음 sentinel 이라 라벨로 나가면 안 된다(화면도 그 값만 숨긴다).
    const scope = String(row?.scope ?? '').trim();
    if (scope && scope !== '__single__') add(scope);
    // ⑤ 원본 위치 — 본문(kind=1) 소스 기준. 정답·풀이 소스는 다른 파일·다른 쪽이라 함께 넣으면
    // 어느 것이 '문제가 실린 자리'인지 갈리지 않는다(모달의 bodyPageId 선택과 같은 관례).
    // page_index 는 0부터라 +1 해서 사람이 읽는 쪽수로 만든다.
    // 「p.24」로 쓰는 이유: 「24쪽」은 지면 번호 「24번」과 한 글자 차이라 눈으로 갈리지 않는다.
    // 다만 PDF 쪽수라 표지·목차가 있으면 교재 인쇄 쪽수와 어긋난다 — 그 한계는 남는다.
    const srcs = Array.isArray(detail?.sources) ? detail.sources : [];
    const bodySrc = srcs.find((x) => Number(x?.kind) === 1) || srcs[0] || null;
    if (bodySrc) {
      const pageIdx = Number(bodySrc.page_index);
      if (Number.isInteger(pageIdx) && pageIdx >= 0) add(`p.${pageIdx + 1}`);
      // 확장자는 분류에 아무것도 보태지 않는다 — 「쎈_문제.pdf」가 아니라 「쎈_문제」로 넣는다
      const fileName = String(bodySrc.file_name ?? '').trim().replace(/\.[^.]+$/, '');
      if (fileName) add(fileName);
    }

    if (next.length === cur.length) { skipped += 1; tick(); continue; } // 이미 다 굳어 있다
    try {
      // eslint-disable-next-line no-await-in-loop
      const res = await patchProblem(id, { extra_labels: next }, getAccessToken);
      if (res === null) return { done, total: list.length, added, skipped, aborted: true, failed: null };
    } catch (e) {
      return { done, total: list.length, added, skipped, aborted: false, failed: e.message || '라벨 저장에 실패했습니다.' };
    }
    added += 1;
    tick();
  }
  return { done, total: list.length, added, skipped, aborted: false, failed: null };
}

// ── 번호(인덱스) 일괄 적용 ──
// planApplySteps가 만든 단계를 그 순서 그대로 하나씩 보낸다. 순서 자체가 충돌을 피하는 장치라
// 병렬로 보내면 안 된다. 한 건이라도 실패하면 즉시 멈추고 어디까지 됐는지를 호출부가 알린다.
// 반환 { done, total, aborted } — aborted는 토큰 만료로 /signin으로 넘어간 경우.
export async function applyNumberSteps(steps, getAccessToken, onProgress) {
  const list = Array.isArray(steps) ? steps : [];
  let done = 0;
  for (const step of list) {
    // eslint-disable-next-line no-await-in-loop
    const d = await patchProblem(step.id, { problem_number: step.to }, getAccessToken);
    if (d === null) return { done, total: list.length, aborted: true };
    done += 1;
    if (onProgress) onProgress(done, list.length);
  }
  return { done, total: list.length, aborted: false };
}

// ── 승인/반려 ──
// POST /api/v2/ocr/problems/{id}/review — { action: 'approve' | 'reject' }. 409 시 message 토스트.
export async function reviewProblem(ocrProbId, action, getAccessToken) {
  return requestJsonWithStatus(
    `/api/v2/ocr/problems/${ocrProbId}/review`,
    { method: 'POST', payload: { action } },
    getAccessToken,
    action === 'approve' ? '승인에 실패했습니다.' : '반려에 실패했습니다.',
  );
}

// ── 승인 일괄 ──
// 승인은 문제당 1회 호출이라 순차로 돌린다. 다만 번호 이동과 달리 서로 독립적이라
// 한 건이 막혀도(409 = 차단 플래그 미해소) 멈추지 않고 나머지를 계속 시도하고,
// 끝에 성공·거부를 모아 돌려준다 — 하나 막혔다고 나머지를 안 할 이유가 없다.
export async function approveProblems(ids, getAccessToken, onProgress) {
  const list = Array.isArray(ids) ? ids : [];
  const rejected = [];
  let done = 0;
  for (const id of list) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const d = await reviewProblem(id, 'approve', getAccessToken);
      if (d === null) return { done, total: list.length, rejected, aborted: true }; // 401 → /signin
      done += 1;
    } catch (e) {
      rejected.push({ id, message: e.message || '승인이 거부되었습니다.' });
    }
    if (onProgress) onProgress(done + rejected.length, list.length);
  }
  return { done, total: list.length, rejected, aborted: false };
}

// ── AI 분석 실행 ──
// POST /api/v2/ocr/projects/{id}/analyze — 실제 분석은 데몬 비동기(analysis-status 폴링).
// 409 = 매칭(bind) 전 / 종료 프로젝트, 404 = 대상 없음.
//
// body {}           = 기본 대상: review_status=200 AND analysis_status ∈ {100 미실행, 500 실패}
// body {ocr_prob_ids}= 지정 재분석: 분석 완료(200)여도 다시 돌린다(처리중 150만 제외)
// 반려하면 백엔드가 AI 생성분을 지우고 analysis_status를 100으로 되돌리므로,
// 반려→재승인만으로도 기본 대상에 자연 복귀한다(그 경로에는 이 인자가 필요 없다).
// userPrompt(선택) — 「고1 과정 개념만」처럼 이 실행에 붙는 지시. 비면 키를 싣지 않는다.
// 서버가 받는다(BE-20260829-reply-fe-0828.md §4-1 표 — 지정 호출 ocr_prob_ids 와 함께 보내도 된다).
// strip 후 500자 이하 · 초과·비문자열은 400. 대상 문제의 analysis_prompt 로 남아 상세에 실려 온다.
// 비면 프로젝트 default_prompt 가 대신 쓰인다(§4-2 5-4).
// 붙는 자리는 프롬프트 말미의 [추가 지시] 블록이고 옵션·규칙이 우선한다(§4-2 6-2).
export async function analyzeProject(projectId, getAccessToken, ocrProbIds = null, userPrompt = null) {
  const ids = Array.isArray(ocrProbIds) ? ocrProbIds.filter((v) => v != null) : [];
  const payload = {};
  if (ids.length > 0) payload.ocr_prob_ids = ids;
  if (userPrompt && userPrompt.trim()) payload.user_prompt = userPrompt.trim();
  return requestJsonWithStatus(
    `/api/v2/ocr/projects/${projectId}/analyze`,
    { method: 'POST', payload },
    getAccessToken,
    'AI 분석 실행에 실패했습니다.',
  );
}

// ── AI 분석 진행률 (폴링용, ocr-status 패턴) ──
// GET /api/v2/ocr/projects/{id}/analysis-status
// data = { project_id, status, total_problems, not_started, pending, processing, done, failed,
//          all_done, rereview_pending } — all_done=true면 폴링 중단 + 문제 목록 새로고침.
// rereview_pending = AI가 정답/풀이를 생성해 재검수(100)로 복귀한 문제 수(배지 노출).
export async function getAnalysisStatus(projectId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`/api/v2/ocr/projects/${projectId}/analysis-status`, {}, getAccessToken);
  return unwrap(res, 'AI 분석 진행률을 불러오지 못했습니다.');
}

// ── 승격(최종 컨펌) 실행 ──
// POST /api/v2/ocr/projects/{id}/promote — body {}. 승격 가능한 문제 전부를 문제은행에 등록.
// 문제당 개별 트랜잭션 → 부분 성공 정상. 멱등(이미 승격된 건 건너뜀, 재호출 안전).
// 승격 조건(백엔드 판정): 검수완료(200) + 차단 flag 없음 + 본문·정답 존재(missing_solution은 허용).
// data = { promoted_now, repromoted_now, total_promoted, no_label_count,
//          promoted[{ocr_prob_id, problem_number, problem_uuid, problem_id, re_promoted, labels, label_count}],
//          skipped[{reason}], failed[{error}] }. 423 = bind 전/종료.
// **2026-08-30 계약 변경**: 승격은 이제 여러 번 된다 — 내용·라벨이 바뀐 행은 같은 은행 문제를
// 덮어쓰고(uuid 유지) 이력 판이 쌓이며, 안 바뀐 행은 skipped 의 'unchanged' 로 나온다.
// **덮어쓰기는 화면에서 유발하지 않는다**(오너 결정 2026-08-30) — 호출부가 이미 등록된 행을
// ocr_prob_ids 에서 빼고 보낸다. force 옵션도 쓰지 않는다.
// ocrProbIds 지정 시 그 문제만 승격(검증 루프용 — BE 회신 Q7). auto_labels:false로 자동 'N번' 라벨 끔(기본 켬).
export async function promoteProject(projectId, getAccessToken, { ocrProbIds = null, autoLabels = true } = {}) {
  const ids = Array.isArray(ocrProbIds) ? ocrProbIds.filter((v) => v != null) : [];
  const payload = {};
  if (ids.length > 0) payload.ocr_prob_ids = ids;
  if (autoLabels === false) payload.auto_labels = false;
  return requestJsonWithStatus(
    `/api/v2/ocr/projects/${projectId}/promote`,
    { method: 'POST', payload },
    getAccessToken,
    '문제 등록에 실패했습니다.',
  );
}

// 승격 skip 사유 한글 매핑 — blocking_flags:<이름들>은 접두 매칭 후 플래그명 번역, 미지 사유는 원문 유지
export function getSkipReasonText(reason) {
  if (typeof reason !== 'string') return String(reason ?? '');
  // unchanged = 이미 등록된 행인데 마지막 등록 뒤 내용·라벨이 그대로다(BE-20260830 §3).
  // 재등록(덮어쓰기)을 화면에서 유발하지 않으므로 정상적으로 자주 나오는 사유다.
  if (reason === 'unchanged') return '변경 없음';
  if (reason === 'not_reviewed') return '검수 미완료';
  if (reason === 'no_body') return '본문 없음';
  if (reason === 'no_answer') return '정답 없음';
  if (reason.startsWith('blocking_flags:')) {
    const names = reason
      .slice('blocking_flags:'.length)
      .split(',')
      .map((s) => getFlagDisplay(s.trim()).text)
      .join(', ');
    return `차단 플래그 미해소(${names})`;
  }
  return reason;
}

// ── 프로젝트 종료 = 최종 시험지 생성 ──
// POST /api/v2/ocr/projects/{id}/close — body { title?, description?, folder_id?, force? } 전부 선택.
//
// **종료가 시험지를 만든다**(BE-20260830-reply-ocr-repromote-history-close.md §4, 오너 결정 ③).
// 승격된 문제 전부를 **교재 순서(scope·번호)** 로 담아 시험지 1장을 만들고 status 500 + 대기 페이지
// 회수까지 **한 트랜잭션**으로 한다 — 시험지 생성이 실패하면 종료도 안 된다.
// 그래서 프론트가 문제은행에서 되찾을 필요가 없다. 라벨로 되찾아 POST /papers 하던 종전 흐름을
// 그대로 두면 **시험지가 두 장** 만들어진다(회신 §4 마지막 줄).
//
// title 기본값 = 프로젝트명. points 는 서버가 넣지 않는다. 작성자 = 호출자.
// 409 = 승격 0건 + force 미지정 → force:true 면 시험지 없이 종료. 423 = 이미 종료.
// 응답 data = { project_id, status:500, total_promoted, cancelled_queue,
//               paper: { paper_id, paper_uuid, title, item_count, folder_id } | null }
// 프로젝트 목록·상세에는 result_paper_id 가 실려 온다.
//
// **2026-09-25 (BE-20260925 §2-4, dev 1e4eb15·a70b53a)**: 등록 0건인데 검수·분석한 미등록 문제가 남아 있으면
// force 만으로는 닫히지 않는다 → 409 data {reason:'unpromoted_reviewed', reviewed, ready, rereview,
// analysis_pending, blocked}. 버린다는 뜻을 따로 밝혀야(discardUnpromoted → discard_unpromoted:true) 닫힌다.
// 409 는 셋이다 — 등록 0건(사유 없음) · unpromoted_reviewed · 시험지 문항 검증 실패(사유 없음, 「시험지 문항 검증 실패」로 시작).
// 옛 서버는 discard_unpromoted 를 모르는 키로 무시한다(force 만으로 닫힘) — 보내도 안전하다.
export async function closeProject(projectId, {
  title, description, folderId, force, discardUnpromoted,
} = {}, getAccessToken) {
  const payload = {};
  const t = typeof title === 'string' ? title.trim() : '';
  if (t) payload.title = t;
  const d = typeof description === 'string' ? description.trim() : '';
  if (d) payload.description = d;
  if (folderId != null) payload.folder_id = folderId;
  if (force) payload.force = true;
  if (discardUnpromoted) payload.discard_unpromoted = true;
  return requestJsonWithStatus(
    `/api/v2/ocr/projects/${projectId}/close`,
    { method: 'POST', payload },
    getAccessToken,
    '프로젝트 종료에 실패했습니다.',
  );
}

// ── 종료 취소 (F13, BE-20260925 §2-5) ──
// POST /api/v2/ocr/projects/{id}/reopen — 시험지 없이 닫힌(500, result_paper 없음) 프로젝트만.
// 되돌린 status: 등록 있음 400 / 분석 돌림 350 / 문제 있음 300 / 문제 없음 200(OCR 한 쪽이라도) 또는 100.
// 응답 {project_id, status}. 409 not_closed(종료 아님) · 409 paper_exists(시험지가 있음 — 시험지 메뉴에서 고칠 일).
// 옛 서버에는 이 경로가 없다 — 404 가 온다(부르는 쪽이 「아직 지원하지 않음」으로 말한다).
export async function reopenProject(projectId, getAccessToken) {
  return requestJsonWithStatus(
    `/api/v2/ocr/projects/${projectId}/reopen`,
    { method: 'POST', payload: {} },
    getAccessToken,
    '종료를 취소하지 못했습니다.',
  );
}

// ── LLM 사용량 ──
// GET /api/v2/ocr/projects/{id}/usage?page=&per_page= — total/by_work_type는 전체 총계,
// by_page만 페이지네이션(page/per_page/total_items/total_pages가 by_page 기준).
export async function fetchProjectUsage(projectId, { page = 1, perPage = 20 } = {}, getAccessToken) {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  const res = await fetchWithTokenRefresh(`/api/v2/ocr/projects/${projectId}/usage?${params}`, {}, getAccessToken);
  return unwrap(res, 'LLM 사용량을 불러오지 못했습니다.');
}

// ── OCR 진행률 (폴링용) ──
// GET /api/v2/ocr/projects/{id}/ocr-status
// data = { project_id, status, total_pages, done, failed, pending, processing, progress(0~1), bind_available }
// bind_available: 전체 페이지 OCR 완료(성공 1↑) 시 true → 매칭(제본) 단계 진입 가능.
// OCR 데몬 미가동 시 pending에 머무름 = 정상 표시(지시서 §6) — 프론트는 진행률만 읽는다.
export async function getOcrStatus(projectId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`/api/v2/ocr/projects/${projectId}/ocr-status`, {}, getAccessToken);
  return unwrap(res, 'OCR 진행률을 불러오지 못했습니다.');
}
